/**
 * ChannelTabBar — 채널 헤더 아래 탭 바
 *
 * 채팅 | Tasks | 게시판 | 캘린더 를 전환하며,
 * 분할/전체 모드 토글을 제공합니다.
 * (게시판 선택/추가 UI는 BoardInlineView 툴바로 이동)
 */
import React from 'react';
import { Trans } from '@lingui/macro';
import {
  MessageSquare, LayoutList, Calendar, ListTodo,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useResponsive } from '../../hooks/useResponsive';

export type ChannelTab = 'chat' | 'tasks' | 'board' | 'calendar';
export type ChannelViewMode = 'expand' | 'split';

interface ChannelTabBarProps {
  activeTab: ChannelTab;
  viewMode: ChannelViewMode;
  /** 1:1 대화방 (direct) 여부 판별용 */
  roomVisibility?: string;
  onTabChange: (tab: ChannelTab) => void;
  onViewModeChange: (mode: ChannelViewMode) => void;
  /** 탭별 미읽음 알림 점(오렌지) 표시 여부 */
  boardHasUnread?: boolean;
  taskHasUnread?: boolean;
  calendarHasUnread?: boolean;
}

const ChannelTabBar: React.FC<ChannelTabBarProps> = ({
  activeTab,
  viewMode,
  roomVisibility,
  onTabChange,
  onViewModeChange,
  boardHasUnread,
  taskHasUnread,
  calendarHasUnread,
}) => {
  const isDirectRoom = roomVisibility === 'direct';
  const { isMobileOrTablet } = useResponsive();

  // 채팅 외 탭은 누르면 항상 분할(split)로 — 모듈 전체화면이던 상태에서 그 탭을 다시 눌러도 분할 복귀.
  // 채팅 탭은 그대로 전환(채팅 전체화면).
  const goTo = (tab: ChannelTab) => {
    if (activeTab !== tab) onTabChange(tab);
    if (tab !== 'chat' && viewMode !== 'split') onViewModeChange('split');
  };

  // 탭 버튼 클래스 — 모바일은 padding/gap 축소 + 줄바꿈 방지 (일본어 ja 라벨이 길어 줄바꿈되던 문제 해결)
  // 주의: 'text-label' 은 tailwind config 에 textColor 도 같은 이름으로 정의되어 있어
  // sm:text-label 을 base 에 쓰면 active 의 text-primary 까지 덮음 → fontSize 만 정의된 text-body-sm 사용.
  const tabBtn = (isActive: boolean) =>
    cn(
      'inline-flex items-center gap-1 px-1.5 py-1 sm:gap-1.5 sm:px-3 sm:py-1.5 text-meta sm:text-body-sm font-medium rounded-md transition-colors whitespace-nowrap shrink-0',
      isActive
        ? 'text-primary bg-primary/8 cursor-default'
        : 'text-muted-foreground hover:text-foreground hover:bg-muted/60 cursor-pointer',
    );

  return (
    <div className="flex items-center gap-0.5 sm:gap-1">
      {/* 채팅 탭 */}
      <button onClick={() => goTo('chat')} className={tabBtn(activeTab === 'chat')}>
        <MessageSquare className="size-3.5" />
        <Trans>Chat</Trans>
      </button>

      {/* Tasks 탭 (1:1 대화방에서는 숨김) */}
      {!isDirectRoom && (
        <button onClick={() => goTo('tasks')} className={cn(tabBtn(activeTab === 'tasks'), 'relative')}>
          <ListTodo className="size-3.5" />
          <Trans>Tasks</Trans>
          {taskHasUnread && (
            <span className="absolute top-0.5 right-0.5 size-1.5 rounded-full bg-primary" aria-label="unread" />
          )}
        </button>
      )}

      {/* 게시판 탭 — 게시판 선택/추가는 BoardInlineView 툴바에서 처리 */}
      <button onClick={() => goTo('board')} className={cn(tabBtn(activeTab === 'board'), 'relative')}>
        <LayoutList className="size-3.5" />
        <Trans>Board</Trans>
        {boardHasUnread && (
          <span className="absolute top-0.5 right-0.5 size-1.5 rounded-full bg-primary" aria-label="unread" />
        )}
      </button>

      {/* 캘린더 탭 */}
      <button onClick={() => goTo('calendar')} className={cn(tabBtn(activeTab === 'calendar'), 'relative')}>
        <Calendar className="size-3.5" />
        <Trans>Calendar</Trans>
        {calendarHasUnread && (
          <span className="absolute top-0.5 right-0.5 size-1.5 rounded-full bg-primary" aria-label="unread" />
        )}
      </button>

    </div>
  );
};

export default ChannelTabBar;
